import { useState, useEffect, useCallback, useRef } from 'react';
import { notebooks as notebooksApi } from '../api/client';
import NOTEBOOK_TEMPLATES from '../utils/notebookTemplates';

const DEFAULT_CATEGORIES = ['plan', 'watchlist', 'notes', 'review'];

export default function NotebookPage() {
  const [notebooks, setNotebooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allCategories, setAllCategories] = useState(DEFAULT_CATEGORIES);
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);
  const fileInputRef = useRef(null);
  const searchTimerRef = useRef(null);

  const fetchNotebooks = useCallback(() => {
    const params = {};
    if (category) params.category = category;
    if (search) params.search = search;
    notebooksApi.list(params)
      .then(res => { setNotebooks(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [category, search]);

  useEffect(() => { fetchNotebooks(); }, [fetchNotebooks]);

  // Load custom categories
  useEffect(() => {
    notebooksApi.categories().then(res => {
      const custom = res.data.filter(c => !DEFAULT_CATEGORIES.includes(c));
      setAllCategories([...DEFAULT_CATEGORIES, ...custom]);
    }).catch(() => {});
  }, [notebooks.length]);

  const handleSearch = (value) => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearch(value), 300);
  };

  const handleNew = (template = null) => {
    const data = {
      title: template ? template.name : 'New Note',
      content: template ? template.content : '',
      category: template ? template.category : 'notes',
    };
    notebooksApi.create(data).then(res => {
      setNotebooks(prev => [res.data, ...prev]);
      setActiveId(res.data.id);
      setEditing({ title: res.data.title, content: res.data.content, category: res.data.category, pinned: res.data.pinned, images: res.data.images || [] });
      setShowTemplates(false);
    });
  };

  const handleSelect = (nb) => {
    setActiveId(nb.id);
    setEditing({ title: nb.title, content: nb.content, category: nb.category, pinned: nb.pinned, images: nb.images || [] });
  };

  const handleSave = () => {
    if (!activeId || !editing) return;
    setSaving(true);
    notebooksApi.update(activeId, editing)
      .then(res => {
        setNotebooks(prev => prev.map(n => n.id === activeId ? res.data : n));
        setSaving(false);
      })
      .catch(() => setSaving(false));
  };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this notebook?')) return;
    notebooksApi.delete(id).then(() => {
      setNotebooks(prev => prev.filter(n => n.id !== id));
      if (activeId === id) { setActiveId(null); setEditing(null); }
    });
  };

  const handleTogglePin = () => {
    if (!activeId) return;
    const nb = notebooks.find(n => n.id === activeId);
    if (!nb) return;
    notebooksApi.update(nb.id, { pinned: !nb.pinned }).then(res => {
      setNotebooks(prev => prev.map(n => n.id === nb.id ? res.data : n));
      if (editing) setEditing(e => ({ ...e, pinned: res.data.pinned }));
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeId) return;
    const formData = new FormData();
    formData.append('image', file);
    notebooksApi.uploadImage(activeId, formData).then(res => {
      setEditing(prev => ({ ...prev, images: res.data.images }));
      // Insert markdown image reference at cursor position
      const imgMd = `\n![${file.name}](${res.data.url})\n`;
      setEditing(prev => ({ ...prev, content: prev.content + imgMd }));
    }).catch(err => console.error('Upload failed:', err));
    e.target.value = '';
  };

  const handleAddCategory = () => {
    if (!newCategory.trim()) return;
    const cat = newCategory.trim().toLowerCase().replace(/\s+/g, '_');
    if (!allCategories.includes(cat)) {
      setAllCategories(prev => [...prev, cat]);
    }
    if (editing) setEditing(prev => ({ ...prev, category: cat }));
    setNewCategory('');
    setShowNewCategory(false);
  };

  if (loading) return <div className="page">Loading notebooks...</div>;

  return (
    <div className="page notebook-page">
      <div className="page-header">
        <h1>Notebook</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-sm" onClick={() => handleNew()}>New Note</button>
          <button className="btn btn-sm" onClick={() => setShowTemplates(v => !v)}>
            {showTemplates ? 'Hide Templates' : 'From Template'}
          </button>
        </div>
      </div>

      {/* Templates */}
      {showTemplates && (
        <div className="notebook-templates">
          {NOTEBOOK_TEMPLATES.map((t, i) => (
            <button key={i} className="notebook-template-btn" onClick={() => handleNew(t)}>
              <span className="notebook-template-name">{t.name}</span>
              <span className={`instrument-type type-${t.category}`}>{t.category}</span>
            </button>
          ))}
        </div>
      )}

      {/* Search + Category filter */}
      <div className="notebook-filters">
        <input type="text" className="notebook-search" placeholder="Search notebooks..."
          onChange={e => handleSearch(e.target.value)} />
        <div className="notebook-categories">
          <button className={`source-tab ${category === '' ? 'active' : ''}`}
            onClick={() => setCategory('')}>All</button>
          {allCategories.map(c => (
            <button key={c} className={`source-tab ${category === c ? 'active' : ''}`}
              onClick={() => setCategory(c)}>{c}</button>
          ))}
        </div>
      </div>

      <div className="notebook-layout">
        {/* Sidebar */}
        <div className="notebook-sidebar">
          {notebooks.length === 0 && <div className="empty-state" style={{ padding: '2rem' }}>No notebooks yet.</div>}
          {notebooks.map(nb => (
            <div key={nb.id}
              className={`notebook-item ${activeId === nb.id ? 'active' : ''}`}
              onClick={() => handleSelect(nb)}>
              <div className="notebook-item-title">
                {nb.pinned && <span style={{ marginRight: 4, fontSize: '0.8em' }}>Pin</span>}
                {nb.title}
              </div>
              <div className="notebook-item-meta">
                <span className={`instrument-type type-${nb.category}`}>{nb.category}</span>
                <span>{new Date(nb.updated_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Editor */}
        <div className="notebook-editor">
          {editing ? (
            <>
              <div className="notebook-editor-header">
                <input className="notebook-title-input" value={editing.title}
                  onChange={e => setEditing(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Title..." />
                <div className="notebook-category-select">
                  <select value={editing.category}
                    onChange={e => {
                      if (e.target.value === '__new__') { setShowNewCategory(true); }
                      else setEditing(prev => ({ ...prev, category: e.target.value }));
                    }}>
                    {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
                    <option value="__new__">+ New category...</option>
                  </select>
                  {showNewCategory && (
                    <div className="notebook-new-category">
                      <input type="text" value={newCategory} onChange={e => setNewCategory(e.target.value)}
                        placeholder="Category name" onKeyDown={e => { if (e.key === 'Enter') handleAddCategory(); }} />
                      <button className="btn btn-sm" onClick={handleAddCategory}>Add</button>
                      <button className="btn-icon" onClick={() => setShowNewCategory(false)}>&times;</button>
                    </div>
                  )}
                </div>
                <button className={`btn btn-sm ${editing.pinned ? 'btn-primary' : ''}`}
                  onClick={handleTogglePin} title={editing.pinned ? 'Unpin' : 'Pin'}>
                  {editing.pinned ? 'Pinned' : 'Pin'}
                </button>
                <button className="btn btn-sm" onClick={() => fileInputRef.current?.click()} title="Upload image">
                  Img
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={handleImageUpload} />
                <button className="btn btn-sm" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(activeId)}>Del</button>
              </div>
              <textarea className="notebook-content-input" value={editing.content}
                onChange={e => setEditing(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Write your notes here... (Markdown supported)" />
              {/* Image gallery */}
              {editing.images?.length > 0 && (
                <div className="notebook-images">
                  {editing.images.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                      <img src={url} alt={`attachment ${i + 1}`} className="notebook-thumb" />
                    </a>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="empty-state" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              Select a notebook or create a new one
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
